import React, {useState, useMemo, useRef} from 'react';
import { withStyles, Typography } from '@material-ui/core';
import { connect } from 'react-redux';
import PermissionsList from './PermissionList';
import { hasPermission, createHistory } from '../../sc-utilities';
import { EditOutlined, DeleteOutline } from '@material-ui/icons';
import * as actions from './store/permissionActions';
import * as action from '../../np-core/Auth/store/authActions';
import { snackActions } from '../../sc-utilities/SnackBar';
import { Redirect } from 'react-router-dom';
import {download} from "../../sc-utilities/download.utils";
import config from "../../../config/config";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='button']": {
            textTransform: 'none',
            textDecoration: 'none'
        }
    },
    button: {
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    }
});

const Permissions = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, columns, activeCustomer } = props;
    let visible = columns && columns['Permissions'];
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, PermissionID: null, redirectPath: "" });
    const [deleteModalState, setDeleteModalState] = useState({
        open: false,
        permissionID: null
    })

    const tableColumns = useMemo(() => {
        return [{
            title: translations["name"] || "Name", field: 'Name',
            hidden: visible ? visible['Name'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["key"] || 'Key', field: 'Key',
            hidden: visible ? visible['Key'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Key ? rowData.Key : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["module"] || 'Module', field: 'Module',
            hidden: visible ? visible['Module'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Module ? rowData.Module : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["locked"] || 'Locked', field: 'Locked',
            hidden: visible ? visible['Locked'] : false,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Locked ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            lookup: { 'private': translations['private'] || 'Private', 'public': translations['public'] || 'Public' },
            defaultFilter: ['private', 'public'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Type ? rowData.Type : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])


    const handleDelete = async (event, rowData) => {
        if (!rowData) {
            setDeleteModalState({ open: false, permissionID: null });
            return;
        }
        setDeleteModalState({ open: true, permissionID: rowData.PermissionID });
    };

    const goToEditPermissions = (id) => {
        createHistory();
        setRedirectOptions({ redirect: true, PermissionsID: null, redirectPath: `/admin/permission/edit?permissionID=${id.PermissionID}` });
    };


    const tableActions = [
        rowData => ({
            tooltip: translations["edit_permission"] || "Edit permission",
            icon: EditOutlined,
            onClick: (evt, rowData) => goToEditPermissions(rowData)
        }),
        rowData => ({
            tooltip: translations["remove_permission"] || "Remove permission",
            icon: DeleteOutline,
            onClick: (evt, rowData) => handleDelete(evt, rowData)
        })
    ]
    const handleDownload = async () => {
        const objectURL = `${config.backend_domain}:${config.backend_port}/permissions/export?ActiveCustomerID=${activeCustomer}`;
        let exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    }

    const goToCreatePermissions = () => {
        createHistory();
        setRedirectOptions({ redirect: true, PermissionsID: null, redirectPath: `/admin/permission/create` });
    };
    const goToUploadPermissions = () => {
        createHistory();
        setRedirectOptions({ redirect: true, PermissionsID: null, redirectPath: `/admin/permission/upload` });
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'permission_list') ?
                <PermissionsList goToCreatePermissions={goToCreatePermissions} handleDownload={handleDownload} deleteModalState={deleteModalState}
                    handleDelete={handleDelete} goToUploadPermissions={goToUploadPermissions} tableColumns={tableColumns} tableActions={tableActions}
                    tableRef={tableRef}
                />
                : null}
        </div >
    );
}

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        permission: state.admin.permission,

    };
};

const mapDispatchToProps = dispatch => {
    return {
        fetchPermissions: filterParams => dispatch(actions.fetchPermissions(filterParams)),
        createPermission: data => dispatch(actions.createPermissions(data)),
        deletePermission: filterParams => dispatch(actions.deletePermissions(filterParams)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        uploadPermissions: data => dispatch(actions.uploadPermissions(data)),
        loadPermission: filterParams => dispatch(actions.loadPermission(filterParams)),
        editPermission: data => dispatch(actions.editPermissions(data))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(Permissions));